// sgi/apps/frontend/ui-enrollment/src/router.ts
import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import {
  Enrollment,
  loadInvitation,
  loadTempSession,
  clearTempSession,
  isTempSessionValid,
  isEnrollmentComplete,
} from './modules/invitation/invitation'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/e/:token',
    component: () => import('./modules/invitation/invitation.vue'),
    meta: { isInvitation: true }, // ← flag para el guard
    children: [
      {
        path: '',
        name: 'Landing',
        component: () => import('./modules/invitation/steps/landing.vue'),
      },
      {
        path: 'identity',
        name: 'Identity',
        component: () => import('./modules/invitation/steps/identity.vue'),
      },
      {
        // M37: activación de cuenta (solo invitaciones ACCOUNT_CREATION)
        path: 'account',
        name: 'Account',
        component: () => import('./modules/invitation/steps/account.vue'),
      },
      {
        path: 'enroll',
        name: 'Enroll',
        component: () => import('./modules/invitation/steps/enroll.vue'),
      },
      {
        path: 'summary',
        name: 'Summary',
        component: () => import('./modules/invitation/steps/summary.vue'),
      },
    ],
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('./modules/invitation/steps/NotFound.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _, next) => {
  // Solo intervenimos rutas de invitación
//   if (!to.path.startsWith('/')) {
//     return next()
//   }

  // ═══ Detectamos rutas de invitación por meta, no por path string ═══
    const isInvitationRoute = to.matched.some((record) => record.meta?.isInvitation)
  if (!isInvitationRoute) {
    return next()
  }

  const token = to.params.token as string
  if (!token) {
    return next({ name: 'NotFound' })
  }

  // Cargar invitación si cambió el token o aún no está
  if (!Enrollment.invitation || Enrollment.token !== token) {
    await loadInvitation(token)
  }

  const invitation = Enrollment.invitation

  // ─── Token inválido: forzar a Landing (donde se muestra el error) ───
  if (!invitation) {
    if (to.name !== 'Landing') {
      return next({ name: 'Landing', params: { token } })
    }
    return next()
  }

  // ═══════════════════════════════════════════════════
  // 1) INVITACIÓN YA USADA → todo va a Summary
  //    (independiente de temp_session, así funciona tras markUsed()
  //     o si el usuario recarga la página en Summary)
  // ═══════════════════════════════════════════════════
  if (invitation.status === 'USED') {
    if (to.name === 'Summary') return next()
    return next({ name: 'Summary', params: { token } })
  }

  // ═══════════════════════════════════════════════════
  // 2) EVALUAR temp_session
  // ═══════════════════════════════════════════════════
  const tempToken = loadTempSession()
  const isValidTemp = tempToken ? isTempSessionValid() : false

  // Si existe pero está vencido, limpiar estado y cookie
  if (tempToken && !isValidTemp) {
    clearTempSession()
    Enrollment.tempSession = ''
    Enrollment.userId = ''
  }

  // ═══════════════════════════════════════════════════
  // 3) CON temp_session VÁLIDO
  // ═══════════════════════════════════════════════════
  if (isValidTemp) {
    // Sincronizar estado reactivo (por si recargó la página)
    if (!Enrollment.tempSession) Enrollment.tempSession = tempToken;
    if (!Enrollment.userId && invitation.target_user?.id) {
      Enrollment.userId = invitation.target_user.id
    }

    // M37: el paso post-identidad depende del tipo de invitación (§9.53)
    const isAccountFlow = invitation.type === 'ACCOUNT_CREATION'
    const postIdentityStep = isAccountFlow ? 'Account' : 'Enroll'

    // Ya identificado: no puede quedarse en Landing ni Identity
    if (to.name === 'Landing' || to.name === 'Identity') {
      return next({ name: postIdentityStep, params: { token } })
    }

    // Cada flujo tiene su propio paso post-identidad — no se cruzan
    if (isAccountFlow && to.name === 'Enroll') {
      return next({ name: 'Account', params: { token } })
    }
    if (!isAccountFlow && to.name === 'Account') {
      return next({ name: 'Enroll', params: { token } })
    }

    // Summary: el flujo facial solo si completó el enrolamiento; el de cuenta
    // llega a Summary con status USED tras activar (regla 1 de arriba)
    if (to.name === 'Summary') {
      if (isAccountFlow) return next({ name: 'Account', params: { token } })
      if (isEnrollmentComplete()) return next()
      return next({ name: 'Enroll', params: { token } })
    }

    return next()
  }

  // ═══════════════════════════════════════════════════
  // 4) SIN temp_session (o fue invalidado arriba)
  //    Solo Landing e Identity son públicas
  // ═══════════════════════════════════════════════════
  if (to.name !== 'Landing' && to.name !== 'Identity') {
    return next({ name: 'Landing', params: { token } })
  }

  return next()
})

export default router